/**
 * AddUser.jsx
 *
 * CHANGES FROM ORIGINAL:
 *   REMOVED: Password field (OTP-based auth — no password needed)
 *   MADE OPTIONAL: Email field (labelled clearly as optional)
 *   KEPT: Name, MobileNo, Address, Role
 *   UPDATED: adminRoute validation must also remove password requirement
 */

import { useState } from "react";
import { createNewUser } from "../..";

const AddUser = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [getform, setform] = useState({
    Name: "",
    MobileNo: "",
    EmailId: "",
    Address: "",
    Role: 1,
  });

  const { Name, MobileNo, EmailId, Address, Role } = getform;

  const getdetails = (name) => (e) => {
    setform({ ...getform, [name]: e.target.value });
  };

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

  const handlesubmit = async (e) => {
    e.preventDefault();

    if (!Name || !Address || !MobileNo) {
      alert("Name, Mobile Number and Address are required.");
      return;
    }
    if (EmailId && !isValidEmail(EmailId)) {
      alert("Please enter a valid email address or leave it empty.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        Name,
        MobileNo,
        Address,
        Role,
        // Send empty string as undefined equivalent — backend handles it
        EmailId: EmailId.trim() || undefined,
        // Password set to placeholder — OTP auth, no real password needed
        password: "otp_user_" + Date.now(),
      };

      const r = await createNewUser(payload);
      if (r?.error) {
        r.error.code ? alert("ErrorCode: " + r.error.code) : alert(r.error);
      } else if (r?.success || r?.message) {
        setform({ Name: "", MobileNo: "", EmailId: "", Address: "", Role: 1 });
        alert(r.success || r.message);
      }
    } catch (e) {
      alert("Error creating user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
    marginBottom: "20px",
  };
  const headerStyle = {
    backgroundColor: "#f8f9fa",
    color: "#2c3e50",
    borderBottom: "2px solid #e9ecef",
    padding: "16px 20px",
    borderRadius: "12px 12px 0 0",
    fontWeight: "600",
    fontSize: "18px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };
  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    fontWeight: "500",
    color: "#495057",
    fontSize: "14px",
  };
  const inputStyle = {
    width: "100%",
    padding: "11px 14px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 0.2s",
  };
  const optionalBadge = {
    fontSize: "11px",
    color: "#aaa",
    fontWeight: 400,
    background: "#f5f5f5",
    padding: "1px 6px",
    borderRadius: "4px",
    marginLeft: "6px",
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span>
          <i className="fas fa-user-plus me-2"></i>Add New User
        </span>
        <button
          onClick={onBack}
          style={{
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            padding: "8px 16px",
            fontSize: "14px",
            cursor: "pointer",
          }}
        >
          <i className="fas fa-arrow-left me-1"></i> Back
        </button>
      </div>

      <div style={{ padding: "24px" }}>
        {/* OTP notice */}
        <div
          style={{
            background: "#fff5f2",
            border: "1px solid #E34607",
            borderRadius: "8px",
            padding: "10px 14px",
            color: "#E34607",
            fontSize: "13px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i className="fas fa-info-circle"></i>
          This user will login via OTP — no password required.
        </div>

        <form onSubmit={handlesubmit}>
          <div className="row">
            {/* Name */}
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>
                <i className="fas fa-user me-1"></i> Full Name{" "}
                <span style={{ color: "#E34607" }}>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={Name}
                onChange={getdetails("Name")}
                placeholder="Enter full name"
                style={inputStyle}
                required
              />
            </div>

            {/* Mobile */}
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>
                <i className="fas fa-phone me-1"></i> Mobile Number{" "}
                <span style={{ color: "#E34607" }}>*</span>
              </label>
              <input
                type="number"
                className="form-control"
                value={MobileNo}
                onChange={getdetails("MobileNo")}
                placeholder="Enter mobile number"
                style={inputStyle}
                required
              />
            </div>

            {/* Email — optional */}
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>
                <i className="fas fa-envelope me-1"></i> Email Address
                <span style={optionalBadge}>Optional</span>
              </label>
              <input
                type="email"
                className="form-control"
                value={EmailId}
                onChange={getdetails("EmailId")}
                placeholder="Enter email (optional)"
                style={{
                  ...inputStyle,
                  border:
                    EmailId && !isValidEmail(EmailId)
                      ? "1.5px solid #f44336"
                      : "1px solid #e0e0e0",
                }}
              />
              {EmailId && !isValidEmail(EmailId) && (
                <small style={{ color: "#f44336" }}>
                  Enter a valid email or leave empty
                </small>
              )}
            </div>

            {/* Address */}
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>
                <i className="fas fa-map-marker-alt me-1"></i> Address{" "}
                <span style={{ color: "#E34607" }}>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={Address}
                onChange={getdetails("Address")}
                placeholder="Enter address"
                style={inputStyle}
                required
              />
            </div>

            {/* Role — display only */}
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>
                <i className="fas fa-user-tag me-1"></i> Role
              </label>
              <input
                type="text"
                className="form-control"
                value="User"
                disabled
                style={{
                  ...inputStyle,
                  background: "#f8f9fa",
                  color: "#6c757d",
                  cursor: "not-allowed",
                }}
              />
            </div>
          </div>

          <p style={{ fontSize: "12px", color: "#aaa", marginBottom: "16px" }}>
            * Required fields
          </p>

          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: loading ? "#6c757d" : "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "12px 28px",
              fontSize: "15px",
              fontWeight: "500",
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Creating...
              </>
            ) : (
              <>
                <i className="fas fa-user-plus"></i> Create User
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddUser;
